import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Grid3x3, Network as NetIcon, Swords, Wallet, LogOut, GraduationCap, MessagesSquare, User as UserIcon, Menu, X, Fingerprint } from 'lucide-react';
import { useArc } from '../context/ArcContext';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { to: '/app', label: 'Command Centre', icon: LayoutDashboard, end: true },
  { to: '/app/protocol', label: 'Protocol Grid', icon: Grid3x3 },
  { to: '/app/goals', label: 'Life Goals', icon: NetIcon },
  { to: '/app/strategy', label: 'Strategy Studio', icon: Swords },
  { to: '/app/financials', label: 'Financials', icon: Wallet },
  { to: '/app/modules', label: 'Modules', icon: GraduationCap },
  { to: '/app/network', label: 'Network', icon: MessagesSquare },
  { to: '/app/myarc', label: 'My Arc', icon: Fingerprint },
  { to: '/app/profile', label: 'Profile', icon: UserIcon }
];

// Mobile bottom-nav: 4 flanks + Protocol Grid as raised center FAB
const MOBILE_LEFT  = [NAV[0], NAV[2]];               // Command Centre, Life Goals
const MOBILE_RIGHT = [NAV[3], NAV[8]];               // Strategy Studio, Profile
const MOBILE_CENTER = NAV[1];                        // Protocol Grid

export default function AppLayout() {
  const { engine, arc, state } = useArc();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const signOut = async () => {
    await logout();
    navigate('/login');
  };

  const isProtocolActive = location.pathname.startsWith('/app/protocol');

  return (
    <div className="relative min-h-screen font-body">
      <div className="myarc-bg" />
      <div className="relative z-10 flex min-h-screen">
        <aside className="hidden md:flex w-[260px] flex-col border-r border-white/5 bg-[#050403]/92 backdrop-blur-xl">
          <div className="px-5 py-6 flex items-center gap-2">
            <img src="/assets/logo.png" alt="" className="h-7 w-auto" />
            <div>
              <div className="font-logo text-[16px] tracking-[0.28em] text-white">MYARC</div>
              <div className="text-[10px] font-data tracking-[0.22em] text-white/40 uppercase">Life OS</div>
            </div>
          </div>

          <div className="px-4 mb-4">
            <div className="panel px-3 py-3">
              <div className="text-[10px] font-data tracking-[0.22em] uppercase text-white/45">Your Arc</div>
              <div className="mt-1 font-head text-[15px]" style={{ color: 'var(--el)' }}>{arc?.key || '·'}</div>
              <div className="text-[11px] font-data text-white/55 mt-0.5">{engine?.name} · {state.nickname || (user?.email ? user.email.split('@')[0] : (state.email ? state.email.split('@')[0] : 'guest'))}</div>
            </div>
          </div>

          <nav className="px-3 flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, end }) => {
              const isProtocol = to === '/app/protocol';
              return (
                <NavLink key={to} to={to} end={end} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''} ${isProtocol ? 'nav-link-gold' : ''}`}>
                  <Icon size={16} /><span className="text-[13.5px]">{label}</span>
                  {isProtocol && <span className="ml-auto eyebrow text-[9px]" style={{ color: '#C7A46D' }}>DAILY</span>}
                </NavLink>
              );
            })}
          </nav>

          <div className="mt-auto p-4 space-y-2">
            <div className="px-3 py-2 text-[11.5px] text-white/55 truncate">{user?.email}</div>
            <button className="btn btn-ghost w-full justify-center" onClick={signOut} data-testid="sidebar-signout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="md:hidden sticky top-0 z-40 bg-[#050403]/92 backdrop-blur-xl border-b border-white/6 px-4 py-3 flex items-center justify-between">
            <button onClick={() => setOpen(true)} className="btn btn-ghost px-2 py-2" data-testid="mobile-menu-btn"><Menu size={18}/></button>
            <div className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="" className="h-6 w-auto" />
              <div className="font-logo text-[14px] tracking-[0.24em]">MYARC</div>
            </div>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/55">{engine?.name}</div>
          </div>

          {open && (
            <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
              <div className="absolute inset-0 bg-[#050403]/85 backdrop-blur-sm" />
              <div className="absolute left-0 top-0 bottom-0 w-[270px] bg-[#050403] border-r border-white/8 p-4 flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <img src="/assets/logo.png" alt="" className="h-6 w-auto" />
                    <div className="font-logo text-[14px] tracking-[0.24em]">MYARC</div>
                  </div>
                  <button onClick={() => setOpen(false)} className="btn btn-ghost px-2 py-2" data-testid="mobile-menu-close-btn"><X size={16}/></button>
                </div>
                <div className="panel px-3 py-3 mb-3">
                  <div className="text-[10px] font-data tracking-widest uppercase text-white/45">Your Arc</div>
                  <div className="font-head text-[14px]" style={{ color: 'var(--el)' }}>{arc?.key}</div>
                </div>
                <nav className="flex-1 flex flex-col gap-1 overflow-y-auto">
                  {NAV.map(({ to, label, icon: Icon, end }) => {
                    const isProtocol = to === '/app/protocol';
                    return (
                      <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''} ${isProtocol ? 'nav-link-gold' : ''}`}>
                        <Icon size={16}/><span className="text-[13.5px]">{label}</span>
                        {isProtocol && <span className="ml-auto eyebrow text-[9px]" style={{ color: '#C7A46D' }}>DAILY</span>}
                      </NavLink>
                    );
                  })}
                </nav>
                <button className="btn btn-ghost w-full justify-center mt-3" onClick={signOut} data-testid="mobile-menu-signout-btn"><LogOut size={14}/> Sign out</button>
              </div>
            </div>
          )}

          <div className="p-5 md:p-8 max-w-[1240px] mx-auto pb-[100px] md:pb-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav with raised center FAB for Protocol Grid */}
      <nav
        aria-label="Primary"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        data-testid="mobile-bottom-nav"
      >
        <div className="relative h-[68px]">
          {/* Bar background with notch */}
          <div className="absolute inset-x-0 bottom-0 h-[68px] bg-[#050403]/96 backdrop-blur-xl border-t border-white/6" />
          {/* Notch glow */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 h-[34px] w-[88px] rounded-b-[40px] pointer-events-none"
               style={{ background: 'radial-gradient(ellipse at top, rgba(199,164,109,0.18), transparent 70%)' }} />

          <div className="relative flex items-end justify-between h-full px-5 pb-2">
            {/* Left two */}
            <div className="flex gap-1 flex-1 justify-around">
              {MOBILE_LEFT.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex flex-col items-center justify-center gap-1 px-2 py-2 transition-colors ${isActive ? 'text-white' : 'text-white/55'}`} data-testid={`mobile-nav-${to.split('/').pop() || 'home'}`}>
                  <Icon size={18} />
                  <span className="text-[10px] tracking-[0.05em]">{label.split(' ')[0]}</span>
                </NavLink>
              ))}
            </div>

            {/* Spacer for FAB */}
            <div className="w-[80px] shrink-0" />

            {/* Right two */}
            <div className="flex gap-1 flex-1 justify-around">
              {MOBILE_RIGHT.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex flex-col items-center justify-center gap-1 px-2 py-2 transition-colors ${isActive ? 'text-white' : 'text-white/55'}`} data-testid={`mobile-nav-${to.split('/').pop() || 'home'}`}>
                  <Icon size={18} />
                  <span className="text-[10px] tracking-[0.05em]">{label.split(' ')[0]}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Center FAB: Protocol Grid */}
          <NavLink
            to={MOBILE_CENTER.to}
            className="absolute left-1/2 -translate-x-1/2 -top-7 flex flex-col items-center group"
            data-testid="mobile-nav-protocol-fab"
          >
            <div
              className={`h-[64px] w-[64px] rounded-full flex items-center justify-center transition-all ${isProtocolActive ? 'scale-105' : 'group-active:scale-95'}`}
              style={{
                background: 'radial-gradient(circle at 30% 30%, #f0c188, #B5925F 75%, #7a5418)',
                boxShadow: isProtocolActive
                  ? '0 0 0 3px rgba(199,164,109,0.55), 0 0 24px rgba(199,164,109,0.6), inset 0 -4px 8px rgba(0,0,0,0.35)'
                  : '0 0 0 3px rgba(199,164,109,0.35), 0 8px 22px rgba(0,0,0,0.55), inset 0 -4px 8px rgba(0,0,0,0.35)',
              }}
            >
              <Grid3x3 size={26} color="#0b0b0b" strokeWidth={2.4} />
            </div>
            <span className="text-[10px] tracking-[0.18em] uppercase mt-1 font-bold" style={{ color: isProtocolActive ? '#C7A46D' : 'rgba(199,164,109,0.85)' }}>Protocol</span>
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
